"use client";

import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import {
  consumeReturnClassIdFromSessionStorage,
  parseClassIdFromClassesReferrer,
  parseFromClassQueryValue,
  PUBLIC_FROM_CLASS_QUERY,
} from "@/lib/utils/publicReturnToClass";
import {
  PUBLIC_LOCATION_RETURN_CLASS_ID,
  PUBLIC_TEACHER_RETURN_CLASS_ID,
} from "@/lib/utils/publicPaths";

/**
 * Resolves originating class id for “Back to [class]” on public teacher/location pages.
 * See `publicReturnToClass.ts` for priority order and limitations.
 */
export function useReturnClassIdForPublicPage(): number | null {
  const searchParams = useSearchParams();

  const fromQuery = useMemo(
    () => parseFromClassQueryValue(searchParams.get(PUBLIC_FROM_CLASS_QUERY)),
    [searchParams],
  );

  const [fallbackId, setFallbackId] = useState<number | null>(null);

  useEffect(() => {
    if (fromQuery != null) {
      try {
        sessionStorage.removeItem(PUBLIC_TEACHER_RETURN_CLASS_ID);
        sessionStorage.removeItem(PUBLIC_LOCATION_RETURN_CLASS_ID);
      } catch {
        /* private mode */
      }
      return;
    }
    const idStorage = consumeReturnClassIdFromSessionStorage();
    if (idStorage != null) {
      setFallbackId(idStorage);
      return;
    }
    const idRef = parseClassIdFromClassesReferrer();
    if (idRef != null) setFallbackId(idRef);
  }, [fromQuery]);

  return fromQuery ?? fallbackId;
}
